import type { DateTime } from "luxon";
import type { ActionContext } from "vuex";
import type { RootState } from "@/store/types";
import type { LogsState } from "@/store/logs/state";
import type { GetFilteredLogsV2, GotFilteredLogs, ColumnarLogs } from "@/models/logs";
import type { DataUsage } from "@/models/common";
import apolloClient from "@/apollo";
import { gql } from "graphql-tag";
import { computed } from "vue";
import { Mutex } from "async-mutex";
import { QueryData } from "@/warehouse/QueryData";
import { RecentFirst } from "@/algorithms/loadingStrategies/RecentFirst";
import * as duckDB from "@/duckDB";

const getFilteredLogsQuery = gql`
    query GetFilteredLogs($input: GetFilteredLogsV2) {
        getFilteredLogsV2(input: $input) {
            __typename
            dataUsage {
                dataProcessed
                dataReturned
            }
            pageInfo {
                hasNextPage
                endCursor
                hasPreviousPage
                startCursor
            }
            columnarLogs{
                attributes{
                    key
                    value
                }
                body
                dynamicColumns {
                    ... on StringDynamicColumn {
                        __typename
                        name
                        values
                    }
                }
                id
                language
                libraryName
                libraryVersion
                message
                resourceAttributes{
                    key
                    value
                }
                serviceInstanceId
                serviceName
                serviceNamespace
                serviceVersion
                severity
                severityNumber
                spanId
                timestamp
                traceFlag
                traceId
            }
        }
    }
`;

const mutex = new Mutex();
let doUpdateLogs = true;

/**
 * Execute the query getFilteredLogs and assigns the Logs to the warehouse.
 * It also keeps pulling query objects from Vuex until all parquet files were loaded.
 *
 * @param context - Logs Vuex context from where the function is being called.
 * @param input - Filename, pageCursor and filters to be applied in the query.
 * @param thisQueryStartedAt - DateTime at which time this query began
 *    executing. Used to prevent old queries from conflicting with more recent
 *    ones due to network race conditions on results.
 * @returns Void or an object with the error message is any.
 */
export default async function executeLogsQuery(
  context: ActionContext<LogsState, RootState>,
  input: GetFilteredLogsV2,
  thisQueryStartedAt: DateTime
): Promise<void | { message: string }> {
  try {
    const loadingStrategy = new RecentFirst();
    if (
      shouldStopQuery(context, thisQueryStartedAt) ||
      queryLimitReached(context)
    ) {
      context.commit("setLoading", false);
      QueryData.updateQueryProgressState("logs", input.filename);
      return;
    }

    const results = await apolloClient.query({
      query: getFilteredLogsQuery,
      variables: {
        input: input,
      },
      fetchPolicy: "no-cache",
    });

    const gotLogs: GotFilteredLogs = results.data.getFilteredLogsV2;
    if (!context.getters["getLoading"]) context.commit("setLoading", true);
    if (
      shouldStopQuery(context, thisQueryStartedAt) ||
      queryLimitReached(context)
    ) {
      context.commit("setLoading", false);
      QueryData.updateQueryProgressState("logs", input.filename);
      return;
    }

    const hasData = Object.values(gotLogs.columnarLogs)
      .filter((v) => Array.isArray(v))
      .some((v) => v.length !== 0);
    if (hasData) {
      await mutex.runExclusive(async () => {
        let tableCreated = false;

        // Table doesn't exist and needs to be created with first batch of data
        const tableExists = await duckDB.tableExists("logs")
        if (!tableExists) {
          await duckDB.createTable("logs", gotLogs.columnarLogs)
          tableCreated = true;
        }

        // Table exists and new data needs to be inserted
        if (!tableCreated) {
          await duckDB.insertIntoTable("logs", gotLogs.columnarLogs)
        }
      })
    }

    const dataUsage: DataUsage = gotLogs.dataUsage as DataUsage;

    context.commit(
      "setDataProcessed",
      context.getters["getDataProcessed"] + dataUsage.dataProcessed
    );
    context.commit(
      "setDataReturned",
      context.getters["getDataReturned"] + dataUsage.dataReturned
    );
    context.commit(
      "setLogsCount",
      context.getters["getLogsCount"] +
      getLogsCount(gotLogs.columnarLogs)
    );
    if(doUpdateLogs){
      doUpdateLogs = false
      context.commit("incUpdateLogs");
      setTimeout(() => {
        doUpdateLogs = true
      }, 1500);
    }

    const hasNextPage = gotLogs.pageInfo.hasNextPage;
    const shouldConsumeNewFile = !hasNextPage;
    if (hasNextPage) {
      const updatedQueryParam = {
        ...input,
        pageCursor: gotLogs.pageInfo.endCursor,
      };
      QueryData.updateQueryData("logs", updatedQueryParam, false);
    } else {
      // Set the filename as consumed
      QueryData.updateQueryData("logs", input, true);
    }

    // Load new clean queryParam object
    const cleanQueryParam = loadingStrategy.getQueryInputs(
      "logs",
      1,
      shouldConsumeNewFile
    ) as GetFilteredLogsV2[];
    if (cleanQueryParam.length > 0)
      await executeLogsQuery(context, cleanQueryParam[0], thisQueryStartedAt);
  } catch (e: any) {
    const error = e.graphQLErrors ? e.graphQLErrors[0] : e;
    return {
      message: error.message,
    };
  }
}

/**
 * Auxiliary function used to stop the parallel queries from running when the environment conditions change.
 *
 * @param context - Logs Vuex context from where the function is being called.
 * @param thisQueryStartedAt - DateTime at which time this query began executing.
 * @returns Indicates whether the current query should be stopped or not.
 */
function shouldStopQuery(
  context: ActionContext<LogsState, RootState>,
  thisQueryStartedAt: DateTime
): boolean {
  const storeLatestQueryStartedAt = computed<DateTime | undefined>(
    () => context.getters["getLatestQueryStartedAt"]
  );
  const storeShouldExecuteQuery = computed(
    () => context.getters["getShouldExecuteQuery"]
  );

  const newerQueryStarted =
    storeLatestQueryStartedAt.value !== undefined &&
    storeLatestQueryStartedAt.value > thisQueryStartedAt;
  const queryShouldCancel =
    storeShouldExecuteQuery.value !== undefined &&
    storeShouldExecuteQuery.value === false;

  return newerQueryStarted || queryShouldCancel;
}

/**
 * Auxiliary function that returns true when the current logs count exceeds the fetchLimit value
 *
 * @param context - Logs Vuex context from where the function is being called.
 * @returns Indicates whether the current fetchLimit value as been reached/exceeded.
 */
function queryLimitReached(context: ActionContext<LogsState, RootState>) {
  return context.getters["getLogsCount"] >= context.getters["getFetchLimit"];
}

/**
 * Get the count of logs in the columnarLogs object.
 *
 * @param columnarLogs - The ColumnarLogs object to count.
 * @returns The number of logs in the ColumnarLogs object.
 */
function getLogsCount(columnarLogs: ColumnarLogs): number {
  return columnarLogs["id"] !== undefined ? columnarLogs["id"].length : 0;
}
